// ---------------------------------------------------------------------------
// step-1-start.tsx — beginner-first create intent capture.
// The user writes what they want, then chooses the path that best matches the
// job. The selection seeds the downstream template/scratch wizard state.
// ---------------------------------------------------------------------------

import React from 'react'
import {
  ArrowRight,
  BriefcaseBusiness,
  ClipboardList,
  Rocket,
  Sparkles,
} from 'lucide-react'
import styles from '../new-project-wizard-page.module.css'
import { useWizard } from '../wizard-state'
import type { ProjectSource } from '../wizard-state'
import type { NewProjectCopy, StarterPathCopy } from '@/i18n/studio-new-project-copy'
import type {
  StudioAppClass,
  StudioRuntimeTarget,
  StudioSecurityProfile,
} from '../../lib/studio-project-plan'
import { useStudioLocale } from '@/i18n'

/**
 * WHAT A STARTER PATH *IS*, with no words in it.
 *
 * The four entries used to carry their own English title, badge, description,
 * example prompt and fallback name. All six of those are copy and now live in
 * `studio-new-project-copy.ts` under `copyKey`; what stays here is the part a
 * translation must not be able to change — which wizard branch the card takes,
 * what it generates, and which icon it wears.
 */
const STARTER_PATH_DEFS = [
  {
    id: 'creator-page',
    copyKey: 'creatorPage',
    source: 'template',
    appClass: 'static',
    runtimeTargets: ['web'],
    securityProfile: 'standard',
    icon: Sparkles,
  },
  {
    id: 'client-app',
    copyKey: 'clientApp',
    source: 'scratch',
    appClass: 'dynamic',
    runtimeTargets: ['web'],
    securityProfile: 'standard',
    icon: BriefcaseBusiness,
  },
  {
    id: 'launch-sell',
    copyKey: 'launchSell',
    source: 'template',
    appClass: 'dynamic',
    runtimeTargets: ['web'],
    securityProfile: 'financial',
    icon: Rocket,
  },
  {
    id: 'agent-ready',
    copyKey: 'teamWorkflow',
    source: 'scratch',
    appClass: 'hybrid',
    runtimeTargets: ['web'],
    securityProfile: 'enterprise',
    icon: ClipboardList,
  },
] as const satisfies ReadonlyArray<{
  id: string
  copyKey: keyof NewProjectCopy['step1']['paths']
  source: ProjectSource
  appClass: StudioAppClass
  runtimeTargets: StudioRuntimeTarget[]
  securityProfile: StudioSecurityProfile
  icon: React.ElementType
}>

export type StarterPath = (typeof STARTER_PATH_DEFS)[number] & StarterPathCopy

/** The four paths, in the reader's language. */
export function starterPaths(
  copy: NewProjectCopy['step1']['paths'],
): StarterPath[] {
  return STARTER_PATH_DEFS.map((def) => ({ ...def, ...copy[def.copyKey] }))
}

interface StarterCardProps {
  path: StarterPath
  copy: NewProjectCopy['step1']
  selected: boolean
  onSelect: (path: StarterPath) => void
}

function StarterCard({ path, copy, selected, onSelect }: StarterCardProps) {
  const Icon = path.icon
  return (
    <button
      type="button"
      className={[
        styles.starterCard,
        selected ? styles.starterCardSelected : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={() => onSelect(path)}
      aria-pressed={selected}
      data-testid={`starter-path-${path.id}`}
    >
      <div className={styles.starterCardHeader}>
        <span className={styles.starterIcon} aria-hidden="true">
          <Icon size={18} strokeWidth={1.75} />
        </span>
        <span className={styles.starterBadge}>{path.badge}</span>
      </div>
      <span className={styles.starterCardTitle}>{path.title}</span>
      <p className={styles.starterCardDesc}>{path.description}</p>
      <span className={styles.starterCardMeta}>
        {path.source === 'template'
          ? copy.startsFromStarter
          : copy.startsFromDescription}
      </span>
      <span className={styles.starterCardAction}>
        {copy.choose}
        <ArrowRight size={14} strokeWidth={1.8} aria-hidden="true" />
      </span>
    </button>
  )
}

export function Step1Start() {
  const { state, dispatch } = useWizard()
  const { strings } = useStudioLocale()
  const copy = strings.newProject.step1
  const paths = starterPaths(copy.paths)

  function handlePromptChange(prompt: string) {
    dispatch({ type: 'SET_START_PROMPT', prompt })
  }

  function handleSelect(path: StarterPath) {
    const prompt = state.startPrompt.trim()
      ? state.startPrompt
      : path.examplePrompt
    dispatch({
      type: 'APPLY_STARTER_PATH',
      id: path.id,
      name: path.title,
      source: path.source,
      prompt,
      fallbackName: path.fallbackName,
      fallbackDescription: path.fallbackDescription,
      appClass: path.appClass,
      runtimeTargets: path.runtimeTargets,
      securityProfile: path.securityProfile,
    })
    // Small delay so user sees the selection before the panel transition
    setTimeout(() => dispatch({ type: 'NEXT' }), 120)
  }

  return (
    <div className={styles.stepContent}>
      <div className={styles.stepHeading}>
        <span className={styles.quickPathKicker}>{copy.kicker}</span>
        <h1 data-route-heading="new-project" className={styles.stepH1}>{strings.routeHeadings.newProject}</h1>
        <p className={styles.stepLead}>{copy.lead}</p>
      </div>

      <div className={styles.promptComposer}>
        <div className={styles.promptLabelRow}>
          <label htmlFor="starter-prompt" className={styles.fieldLabel}>
            {copy.promptLabel}
          </label>
          <span className={styles.fieldOptional}>{copy.promptOptional}</span>
        </div>
        <textarea
          id="starter-prompt"
          className={styles.intentTextarea}
          value={state.startPrompt}
          onChange={(event) => handlePromptChange(event.target.value)}
          placeholder={copy.promptPlaceholder}
          rows={4}
          data-testid="new-project-starter-prompt"
        />
      </div>

      <div className={styles.starterGrid}>
        {paths.map((path) => (
          <StarterCard
            key={path.id}
            path={path}
            copy={copy}
            selected={state.starterPathId === path.id}
            onSelect={handleSelect}
          />
        ))}
      </div>

      <p className={styles.starterHint}>{copy.hint}</p>
    </div>
  )
}
